import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Users } from 'lucide-react';
import { candidateLibraryApi } from '../../lib/api/candidateLibraryApi';
import CandidatesTable from '../screenings/CandidatesTable';
import AICandidateSearchBar from '../../components/results/AICandidateSearchBar';
import useCandidateScoreModals from '../../hooks/useCandidateScoreModals';

const DEFAULT_PAGE_SIZE = 25;

const noop = () => {};

const CandidateLibrary = () => {
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const handleSearchSubmit = useCallback(() => {
    setPage(1);
    setActiveSearch(searchValue.trim());
  }, [searchValue]);

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setActiveSearch('');
    setPage(1);
  }, []);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['candidate-library', page, activeSearch],
    queryFn: () =>
      candidateLibraryApi.list({
        page,
        limit: DEFAULT_PAGE_SIZE,
        q: activeSearch || undefined,
      }),
    keepPreviousData: true,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const hasMore = data?.hasMore ?? (totalPages === 0 ? false : page < totalPages);
  const showingFrom = total === 0 ? 0 : (page - 1) * DEFAULT_PAGE_SIZE + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * DEFAULT_PAGE_SIZE, total);

  const screeningMeta = useMemo(
    () => ({
      hasResumeScoring: true,
      includeAssessment: true,
      includeInterview: true,
    }),
    []
  );

  const tableOptions = useMemo(
    () => ({
      showStatus: false,
      showShortlistedDate: false,
      hideColumnFilters: true,
      showInlineEmptyState: true,
      hideRowActions: true,
      columnVisibility: {
        documents: false,
        candidateDetails: false,
        screening: true,
        companyName: false,
        location: false,
        email: true,
        phone: true,
        appliedDate: true,
        proctoring: false,
        completedAt: false,
      },
    }),
    []
  );

  const [scoreFilters, setScoreFilters] = useState({});
  const [metaFilters, setMetaFilters] = useState({});
  const [openScoreFilterId, setOpenScoreFilterId] = useState(null);
  const [openMetaFilterId, setOpenMetaFilterId] = useState(null);
  const [openActionsCandidate, setOpenActionsCandidate] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [shortlistDecisions, setShortlistDecisions] = useState({});
  const [shortlistNotes, setShortlistNotes] = useState({});

  const parseExperienceYearsForFilter = useCallback((candidate) => candidate?.experienceYears ?? null, []);
  const parseAgeYearsForFilter = useCallback((candidate) => candidate?.ageYears ?? null, []);
  const parseCurrentCtcLacsForFilter = useCallback((candidate) => candidate?.currentCtcLacs ?? null, []);

  const {
    openCandidateResumeBreakdown,
    handleInlineViewAssessmentScore,
    handleInlineViewAssessmentProctoring,
    openInterviewSessionForCandidate,
    ScoreModals,
  } = useCandidateScoreModals();

  const renderTable = () => (
    <CandidatesTable
      rows={rows}
      screeningMeta={screeningMeta}
      scoreFilters={scoreFilters}
      setScoreFilters={setScoreFilters}
      metaFilters={metaFilters}
      setMetaFilters={setMetaFilters}
      openScoreFilterId={openScoreFilterId}
      setOpenScoreFilterId={setOpenScoreFilterId}
      openMetaFilterId={openMetaFilterId}
      setOpenMetaFilterId={setOpenMetaFilterId}
      openActionsCandidate={openActionsCandidate}
      setOpenActionsCandidate={setOpenActionsCandidate}
      actionMenuPosition={actionMenuPosition}
      setActionMenuPosition={setActionMenuPosition}
      parseExperienceYearsForFilter={parseExperienceYearsForFilter}
      parseAgeYearsForFilter={parseAgeYearsForFilter}
      parseCurrentCtcLacsForFilter={parseCurrentCtcLacsForFilter}
      openCandidateResumeBreakdown={openCandidateResumeBreakdown}
      openInterviewSessionForCandidate={openInterviewSessionForCandidate}
      handleRemoveFromShortlistRow={noop}
      handleInlineViewAssessmentScore={handleInlineViewAssessmentScore}
      handleInlineViewAssessmentProctoring={handleInlineViewAssessmentProctoring}
      setCandidateToDelete={noop}
      setCandidateToResend={noop}
      setCandidateToShortlist={noop}
      setShowResendConfirmModal={noop}
      setShowShortlistConfirmModal={noop}
      documentStatusMap={{}}
      onViewDocumentsCandidate={noop}
      shortlistDecisions={shortlistDecisions}
      setShortlistDecisions={setShortlistDecisions}
      shortlistNotes={shortlistNotes}
      setShortlistNotes={setShortlistNotes}
      patchCandidateShortlistField={async () => false}
      options={tableOptions}
    />
  );

  const renderBody = () => {
    if (error) {
      return (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.response?.data?.message || 'Failed to load candidates. Please try again.'}
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-[#007BFF]" />
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {renderTable()}
      </div>
    );
  };

  return (
    <div data-testid="candidate-library-page">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#007BFF] uppercase tracking-wide">
              <Users className="h-4 w-4" />
              Candidate Library
            </div>
            <h1 className="mt-2 text-2xl font-semibold text-gray-900">Search across every screening</h1>
            <p className="mt-1 text-sm text-gray-600">
              Quickly locate any applicant by name, email, or phone across all job postings you can access.
            </p>
          </div>
          <div className="text-sm text-gray-600">
            <div className="font-semibold text-gray-900 text-lg">{total.toLocaleString()} candidates</div>
            <div className="text-xs text-gray-500">Includes every job you have permission to view.</div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <AICandidateSearchBar
            value={searchValue}
            onChange={setSearchValue}
            onSearch={handleSearchSubmit}
            onClear={handleClearSearch}
            isSearching={isFetching}
            isActive={Boolean(activeSearch)}
            placeholder="Search candidate by name, email, or phone"
            disabled={isFetching && !rows.length}
            searchButtonLabel="Search"
          />
        </div>

        {renderBody()}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm text-gray-600">
          <div>
            {total > 0 ? (
              <span>
                Showing <strong>{showingFrom}</strong>–<strong>{showingTo}</strong> of{' '}
                <strong>{total.toLocaleString()}</strong> candidates
              </span>
            ) : (
              <span>No candidates match your current search.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1 || isFetching}
              className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
                page === 1 || isFetching
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <div className="text-xs text-gray-500">
              Page {totalPages === 0 ? 0 : page} of {totalPages || 1}
            </div>
            <button
              type="button"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={!hasMore || isFetching}
              className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
                !hasMore || isFetching
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <ScoreModals />
    </div>
  );
};

export default CandidateLibrary;
